import React, {useState, useEffect, useMemo} from 'react';
import routes from './pages/routes';
import {useDispatch, useSelector} from 'react-redux';
import {changeLanguage, loadAuth} from './components/auth/reducers';
import {Route, Switch, Redirect} from 'react-router-dom';
import AuthModal from './pages/auth/components/AuthModal';
import PartnerAuthModal from './pages/auth/components/PartnerAuthModal';
import partnerUtils from './utils/partnerUtils';
import GeneralAuthModal from './pages/auth/components/GeneralAuthModal';
import {notifyAPPWithCommand} from './utils/notifyAppWithCommand';
import {withRouter} from 'react-router';
import useWindowWidth from './components/hook/useWindowWidth';
import cookieUtils from './utils/cookieUtils';
import langUtils from './utils/langUtils';
import TagManager from 'react-gtm-module';
import smbUtils from './utils/smbUtils';
import {Trans, useTranslation} from 'react-i18next';
import globals from './utils/config/globals';
import _get from 'lodash/get';
import accountDao from './dao/accountDao';
import _isEmpty from 'lodash/isEmpty';
import Loader from './components/spinner/Loader';

const tagManagerArgs = {
  gtmId: 'GTM-TQ5H2D4',
};
let loadAuthTriggered = false;
const DISABLE_AUTH_CHECK_URLS = [
  '/eyewear/image-spec-shopify',
  '/epf/msr-template-requests',
];

export default withRouter(({location}) => {
  const {i18n} = useTranslation();
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {customer} = auth;
  const functionType = customer.functionType;
  const functionTypeName = smbUtils.getFunctionTypeI18n(functionType);
  const accountId = customer.accountId;
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const signInWithApp = params.get('signInWithApp');
  const lang = params.get('lang');
  const loaded = params.get('loaded');
  const [timeLeft, setTimeLeft] = useState(10);
  const availableLang = ['enu', 'jpn', 'esp', 'fra', 'chs', 'cht'];
  const width = useWindowWidth();
  const [showHint, setShowHint] = useState(cookieUtils.getCookie('mb_hint'));
  const account = useSelector(
    state => state.components.auth.accountSummary.account
  );
  const memoizedRoutes = useMemo(() => routes(auth), [auth]);
  const isPassAuthCheckRoute = DISABLE_AUTH_CHECK_URLS.some(url =>
    location.pathname.startsWith(url)
  );

  useEffect(() => {
    if (
      !_isEmpty(account) &&
      !_isEmpty(lang) &&
      availableLang.includes(lang) &&
      _get(globals.languages2.find(o => o.value === lang), 'i18n') !==
        i18n.language
    ) {
      accountDao.updateAccount({...account, languageType: lang}).then(() => {
        setTimeout(() => {
          dispatch(changeLanguage(lang));
          dispatch(loadAuth());
        }, 1000);
      });
    }
  }, [lang, account]);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    if (timeLeft > 0) {
      const timerId = setTimeout(() => {
        setTimeLeft(prevTime => prevTime - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    } else {
      window.location.href = '/';
    }
  }, [loaded, timeLeft]);

  useEffect(() => {
    if (loaded) {
      document.body.style.overflow = 'hidden';
    }
  }, [loaded]);

  useEffect(() => {
    if (smbUtils.isInYmkApp()) {
      cookieUtils.setCookie(
        'appLang',
        langUtils.convertLanguageCode(smbUtils.getUserAgentInApp(4))
      );
    }
  }, []);

  window.onload = function() {
    if (smbUtils.isInYmkApp()) {
      setTimeout(() => {
        notifyAPPWithCommand('webReady');
      }, 1500);
    }
  };

  useEffect(() => {
    if (!smbUtils.isWithGa()) return;
    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    if (!auth || !customer || !functionType) return;
    if (smbUtils.isInYmkApp() && functionType === 'SKINCARE_PRO') {
      notifyAPPWithCommand(
        'disableBackForwardList',
        JSON.stringify({disable: signInWithApp === 'true'})
      );
    }
  }, [location, auth, functionType]);

  if (!isPassAuthCheckRoute) {
    if (!auth || !auth.account) {
      if (!loadAuthTriggered) {
        dispatch(loadAuth());
        loadAuthTriggered = true;
      }
      return <Route render={() => <div />} />;
    }
  }
  if (auth.isDisabled) {
    return <GeneralAuthModal />;
  }

  const isPartnerSite = partnerUtils.isPartnerSite();
  const allowPassCheck = loaded || isPassAuthCheckRoute || auth.isSuperAccount;

  return (
    <div>
      {width <= 600 && (!showHint || showHint != accountId) && (
        <div className="suggest-hint">
          <div>
            This page won't work well on a mobile device. Please switch to a
            desktop device to view this page properly.
          </div>
          <button
            onClick={() => {
              setShowHint(accountId);
              cookieUtils.setCookie('mb_hint', accountId);
            }}
          >
            close
          </button>
        </div>
      )}
      {!allowPassCheck && (
        <div>
          {isPartnerSite ? (
            <PartnerAuthModal auth={auth} />
          ) : (
            <AuthModal auth={auth} />
          )}
        </div>
      )}
      {loaded && (
        <div className="dark-overlay">
          <div className="dark-overlay-item">
            <div className="" style={{margin: 'auto'}}>
              <h1 className="kt-margin-b-30">
                <Trans i18nKey="p.home.welcome">
                  Welcome to Online Service {{functionTypeName}}!
                </Trans>
              </h1>
              <h2>
                <Trans i18nKey="p.home.payment.progress">
                  Payment in progress. Your subscription will be activated
                  shortly.
                </Trans>
              </h2>
              <div className="kt-margin-30 text-center">
                <Loader />
              </div>
              <h2>
                <Trans i18nKey="p.home.payment.progress.time">
                  {{timeLeft}} seconds remaining...
                </Trans>
              </h2>
            </div>
          </div>
        </div>
      )}
      <Switch>
        {memoizedRoutes}
        <Redirect to={isPartnerSite ? '/partner' : '/'} />
      </Switch>
    </div>
  );
});
